import { rotuloSeveridade, tomSeveridade } from "@sentinela/shared/rotulos";
import { fraseDoErro } from "../../lib/erro-amigavel";
import type { ButtonHTMLAttributes, HTMLAttributes, InputHTMLAttributes, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";

export { Drawer } from "./Drawer.js";
export type { LarguraDrawer } from "./Drawer.js";
export { BotaoCsv } from "./BotaoCsv.js";
export { DatePicker } from "./DatePicker.js";
export type { IntervaloData } from "./DatePicker.js";
export { linhaClicavel, Paginacao, ThOrdenavel, ThServidor } from "./Tabela.js";
export { BuscaServidor } from "./BuscaServidor.js";
export { GraficoBarras, TabelaDoGrafico, maximoRedondo } from "./GraficoBarras.js";
export { CabecalhoDaPagina } from "./CabecalhoDaPagina.js";
export { Abas, PainelDaAba } from "./Abas.js";
export type { AbaItem } from "./Abas.js";
export type { PontoBarra, ColunaGrafico } from "./GraficoBarras.js";

/** Wrapper minimalista pra concatenar classes condicionalmente */
function cn(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

// ============================================================================
// Card
// ============================================================================

export function Card({
  className,
  children,
  hover,
  ...rest
}: HTMLAttributes<HTMLDivElement> & { hover?: boolean }) {
  return (
    <div className={cn(hover ? "card-hover" : "card", className)} {...rest}>
      {children}
    </div>
  );
}

export function CardHeader({ titulo, descricao, acao }: { titulo: string; descricao?: string; acao?: React.ReactNode }) {
  return (
    <div className="px-5 py-4 border-b border-ink-200 flex items-start justify-between gap-3">
      <div>
        <h3 className="text-base font-display font-bold text-ink-800 leading-tight">{titulo}</h3>
        {descricao && <p className="text-xs text-muted mt-1">{descricao}</p>}
      </div>
      {acao}
    </div>
  );
}

export function CardBody({ className, children }: { className?: string; children: React.ReactNode }) {
  return <div className={cn("p-5", className)}>{children}</div>;
}

// ============================================================================
// Button
// ============================================================================

export type Variante = "primary" | "outline" | "ghost" | "danger" | "black";
export type Tamanho = "sm" | "md" | "lg";

interface BotaoProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: Variante;
  tamanho?: Tamanho;
  carregando?: boolean;
}

/**
 * Walk recursivo nos children pra extrair texto simples — usado pra dar
 * tooltip default ao Botao. Filhos React podem ser strings, números, JSX
 * (ex: ícone + texto). Concatena tudo que for string/number, ignora nulls.
 *
 * Convenção do produto: TODO botão tem `title` nativo no hover. O default
 * é o próprio texto do botão; quando o label não explica "por quê/
 * consequência" (ex: "Recalcular" → "Recarrega overview ignorando cache"),
 * o caller passa `title="..."` explícito pra explicar melhor.
 */
function extrairTextoBotao(children: React.ReactNode): string {
  if (children == null || typeof children === "boolean") return "";
  if (typeof children === "string" || typeof children === "number") {
    return String(children);
  }
  if (Array.isArray(children)) {
    return children.map(extrairTextoBotao).filter(Boolean).join(" ").trim();
  }
  if (typeof children === "object" && "props" in children) {
    const props = (children as { props: { children?: React.ReactNode } }).props;
    return extrairTextoBotao(props.children);
  }
  return "";
}

export function Botao({
  variante = "primary",
  tamanho = "md",
  carregando,
  disabled,
  className,
  children,
  title,
  ...rest
}: BotaoProps) {
  const variClass = {
    primary: "btn-primary",
    outline: "btn-outline",
    ghost: "btn-ghost",
    danger: "btn-danger",
    black: "btn-black",
  }[variante];
  const tamClass = tamanho === "sm" ? "btn-sm" : tamanho === "lg" ? "btn-lg" : "";
  // Tooltip default: texto do próprio botão. Caller que quer explicação
  // mais rica passa `title="..."` direto — exemplo no header do Geofeed.
  const tituloEfetivo = title ?? (extrairTextoBotao(children).trim() || undefined);
  return (
    <button
      className={cn(variClass, tamClass, className)}
      disabled={disabled || carregando}
      title={tituloEfetivo}
      {...rest}
    >
      {carregando ? (
        <>
          <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-r-transparent" />
          {children}
        </>
      ) : (
        children
      )}
    </button>
  );
}

// ============================================================================
// Input / Select / Textarea
// ============================================================================

export function Campo({
  label,
  hint,
  erro,
  required,
  className,
  children,
}: {
  label?: string;
  hint?: string;
  erro?: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={cn("flex flex-col gap-1", className)}>
      {label && (
        <span className="text-xs font-bold text-ink-700">
          {label}
          {required && <span className="text-magenta-600 ml-0.5">*</span>}
        </span>
      )}
      {children}
      {hint && !erro && <span className="text-xs text-muted">{hint}</span>}
      {erro && <span className="text-xs text-estado-erro-fg">{erro}</span>}
    </label>
  );
}

export function Entrada({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cn("input", className)} {...rest} />;
}

export function Selecao({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={cn("input", className)} {...rest}>
      {children}
    </select>
  );
}

export function AreaTexto({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cn("input", className)} {...rest} />;
}

// ============================================================================
// Badge / Pill
// ============================================================================

export type TomBadge = "magenta" | "ink" | "success" | "warning" | "danger" | "info" | "critico";

export function Badge({ tom = "ink", children }: { tom?: TomBadge; children: React.ReactNode }) {
  const cls = {
    magenta: "badge-magenta",
    ink: "badge-ink",
    success: "badge-success",
    warning: "badge-warning",
    danger: "badge-danger",
    info: "badge-info",
    critico: "badge-critico",
  }[tom];
  return <span className={cls}>{children}</span>;
}

/**
 * Selo de severidade de alerta — UM lugar para rótulo (com acento) e tom (SEN-12, 11/10/2026). Troca só a
 * EXIBIÇÃO: o valor que vai para a API, o filtro e o CSV continuam sendo o código (`critica`).
 * Crítica é vermelho CHEIO e Alta é vermelho tingido (antes as duas tinham a mesma cor).
 */
export function BadgeSeveridade({ valor }: { valor: string | null | undefined }) {
  return <Badge tom={tomSeveridade(valor)}>{rotuloSeveridade(valor)}</Badge>;
}

// ============================================================================
// Estados de carregamento / vazio
// ============================================================================

export function Carregando({ texto = "Carregando…" }: { texto?: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-muted">
      <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-r-transparent" />
      {texto}
    </div>
  );
}

export function VazioEstado({
  titulo,
  descricao,
  acao,
}: {
  titulo: string;
  descricao?: string;
  acao?: React.ReactNode;
}) {
  return (
    <div className="card p-8 text-center">
      <p className="font-display font-bold text-ink-800 text-base">{titulo}</p>
      {descricao && <p className="text-sm text-muted mt-1">{descricao}</p>}
      {acao && <div className="mt-4">{acao}</div>}
    </div>
  );
}

/**
 * Faixa de erro (`role="alert"`). Texto que é um CÓDIGO técnico ("http_500", "erro_interno: …") vira frase
 * (`fraseDoErro`) e o código original fica no `title`; frase em português passa como está. Com `aoTentar`, mostra
 * "Tentar de novo" (erro de CARGA: a tela mantém a leitura anterior, se houver, e nunca finge "vazio" — DO-01).
 */
export function AlertaErro({
  children,
  aoTentar,
}: {
  children: React.ReactNode;
  aoTentar?: () => void;
}) {
  const crua = typeof children === "string" ? children : null;
  const texto = crua !== null ? fraseDoErro(crua) : children;
  return (
    <div
      role="alert"
      title={crua !== null && texto !== crua ? crua : undefined}
      className="flex flex-wrap items-center justify-between gap-2 rounded border border-estado-erro-borda bg-estado-erro-bg px-4 py-2 text-sm text-estado-erro-fg"
    >
      <span className="min-w-0">{texto}</span>
      {aoTentar && (
        <button type="button" onClick={aoTentar} className="btn btn-outline-danger btn-sm shrink-0">
          Tentar de novo
        </button>
      )}
    </div>
  );
}

export function AlertaSucesso({ children }: { children: React.ReactNode }) {
  return (
    <div role="status" className="rounded border border-estado-sucesso-borda bg-estado-sucesso-bg px-4 py-2 text-sm text-estado-sucesso-fg">
      {children}
    </div>
  );
}

// ============================================================================
// BuscaLivre — campo de busca padrão do app
//
// Convenção do produto: filtros só ativam a partir de 3 caracteres.
// Abaixo disso, mostra hint "faltam N". Acima, mostra "X de Y".
// Use junto com `filtrarBusca()` pra varrer N campos do item.
// ============================================================================

export function BuscaLivre({
  valor,
  onChange,
  placeholder,
  total,
  filtrado,
  className,
  rotulo = "Busca",
}: {
  /** Nome acessível do campo: telas com mais de uma busca passam um nome para cada (SEB-36). */
  rotulo?: string;
  valor: string;
  onChange: (v: string) => void;
  placeholder?: string;
  /** Quantidade total de itens (antes do filtro) */
  total?: number;
  /** Quantidade após o filtro — pra mostrar "X de Y" */
  filtrado?: number;
  className?: string;
}) {
  const trim = valor.trim();
  const ativa = trim.length >= 3;
  // Dica em português: "≥ 3 chars" é notação, não instrução — e este campo é
  // usado por gente de várias áreas, não só por quem lê símbolo matemático.
  const hint =
    valor.length === 0
      ? "3 letras ou mais"
      : trim.length < 3
        ? `faltam ${3 - trim.length} ${3 - trim.length === 1 ? "letra" : "letras"}`
        : total !== undefined && filtrado !== undefined
          ? `${filtrado} de ${total}`
          : `filtrando…`;

  return (
    <div className={cn("flex items-center gap-3 flex-wrap", className)}>
      <div className="relative flex-1 min-w-[280px] max-w-[480px]">
        <input
          type="text"
          value={valor}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder ?? "Buscar… (≥ 3 caracteres)"}
          className="input pr-24"
          aria-label={rotulo}
        />
        <span
          className={cn(
            "absolute right-3 top-1/2 -translate-y-1/2 text-xs",
            ativa ? "text-magenta-600" : "text-muted",
          )}
        >
          {hint}
        </span>
      </div>
      {valor && (
        <button
          type="button"
          onClick={() => onChange("")}
          className="text-xs text-muted hover:text-magenta-600 transition-colors"
        >
          limpar
        </button>
      )}
    </div>
  );
}

/**
 * Helper genérico pra varrer N campos de um item contra o termo de busca.
 * Convenção do app: só filtra com termo ≥ 3 chars; senão retorna todos.
 *
 * @example
 * const filtrados = filtrarBusca(busca, lista, (item) => [
 *   item.nome, item.email, item.documento, item.tags
 * ]);
 */
export function filtrarBusca<T>(
  termo: string,
  lista: T[],
  extrair: (item: T) => Array<string | number | null | undefined>,
): T[] {
  const norm = termo.trim().toLowerCase();
  if (norm.length < 3) return lista;
  return lista.filter((item) => {
    const campos = extrair(item).flat();
    return campos.some((c) => c != null && String(c).toLowerCase().includes(norm));
  });
}

// ============================================================================
// Logo
// ============================================================================

/**
 * `sobreEscuro`: o cabeçalho móvel é SEMPRE ink-900, também no tema claro. Sem isto o
 * "Sentinela" saía em ink-800 sobre ink-900 (1,27:1, quase invisível, medido em
 * 10/10/2026 em 390 px) e o logo dependia do `dark:` que não se aplica ali.
 */
export function LogoSentinela({ tamanho = "md", sobreEscuro = false }: { tamanho?: "sm" | "md" | "lg"; sobreEscuro?: boolean }) {
  const sz = tamanho === "sm" ? "h-7" : tamanho === "lg" ? "h-12" : "h-9";
  return (
    <div className={cn("inline-flex items-center gap-3", sz)}>
      {/* Logo USE oficial — magenta em light, branco em dark via filter */}
      <img
        src="/logo-use.svg"
        alt="Use Telecom"
        className={cn("h-full w-auto", sobreEscuro ? "hidden" : "dark:hidden")}
      />
      <img
        src="/logo-use-white.svg"
        alt="Use Telecom"
        className={cn("h-full w-auto", sobreEscuro ? "block" : "hidden dark:block")}
      />
      <div className={cn("flex flex-col leading-none border-l pl-3", sobreEscuro ? "border-ink-600" : "border-ink-300 dark:border-ink-200")}>
        <span className={cn("font-display font-bold text-base", sobreEscuro ? "text-white" : "text-ink-800")}>
          Sentinela
        </span>
        <span className={cn("text-xs uppercase tracking-uppercase font-bold mt-0.5", sobreEscuro ? "text-magenta-300" : "text-magenta-600 dark:text-magenta-300")}>
          Security Ops
        </span>
      </div>
    </div>
  );
}
